import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import PayWithRazorpay from "@/components/PayWithRazorpay";
import { CheckCircle } from "@phosphor-icons/react";

const STATUSES = ["placed", "in_production", "quality_check", "dispatched", "delivered"];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [design, setDesign] = useState(null);

  const load = async () => {
    const { data: o } = await api.get(`/orders/${id}`);
    setOrder(o);
    const { data: inv } = await api.get(`/orders/${id}/invoice`);
    setInvoice(inv);
    const designs = await api.get(`/briefs/${o.brief_id}/designs`);
    const d = designs.data.find(x => x.id === o.design_id);
    setDesign(d);
  };
  useEffect(() => { load(); }, [id]);

  const updateStatus = async (s) => {
    try {
      await api.patch(`/orders/${id}/status`, { status: s });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  if (!order) return (<div className="min-h-screen bg-[var(--pf-bg)]"><Nav/><div className="p-12 pf-label">Loading…</div></div>);

  const currentIdx = STATUSES.indexOf(order.status);

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <Link to={user?.role==="admin"?"/admin/orders":"/orders"} className="pf-label hover:text-[var(--pf-brand)]">← Orders</Link>
        <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-4 mt-4 mb-10">
          <div className="md:col-span-8">
            <div className="pf-label mb-2">Order</div>
            <h1 className="text-4xl font-black tracking-tighter">#{order.id.slice(0,8).toUpperCase()}</h1>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div className="pf-label">Total (incl. GST)</div>
            <div className="font-mono text-3xl font-black">₹{invoice?.total?.toLocaleString?.() || order.total_amount}</div>
            {order.payment_status === "paid" && (
              <div
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded"
                data-testid="payment-paid-badge"
              >
                <CheckCircle size={14} weight="fill"/> Paid
              </div>
            )}
          </div>
        </div>

        {/* Payment section */}
        {order.payment_status !== "paid" && (
          <div className="border-2 border-[var(--pf-brand)] bg-[var(--pf-brand)]/5 p-6 mb-8" data-testid="payment-section">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
              <div>
                <div className="pf-label mb-1">Payment required</div>
                <h2 className="text-2xl font-black tracking-tight">Complete your payment</h2>
                <p className="text-sm text-[var(--pf-text-2)] mt-2">
                  Your order won't enter production until payment is confirmed.
                </p>
              </div>
              <div className="text-right">
                <div className="pf-label">Amount due</div>
                <div className="font-mono text-2xl font-black">₹{invoice?.total?.toLocaleString?.() || order.total_amount}</div>
              </div>
            </div>
            <PayWithRazorpay
              orderId={order.id}
              userInfo={{ name: user?.name, email: user?.email }}
              onSuccess={() => load()}
            />
          </div>
        )}

        {/* Status timeline */}
        <div className="border border-[var(--pf-border)] p-6 mb-8">
          <div className="pf-label mb-4">Status timeline</div>
          <div className="grid grid-cols-5 gap-2">
            {STATUSES.map((s, i) => (
              <div key={s} className="text-center">
                <div className={`h-1 w-full ${i <= currentIdx ? "bg-[var(--pf-brand)]" : "bg-[var(--pf-border)]"}`}></div>
                <div className={`mt-3 text-[10px] uppercase tracking-widest font-bold ${i <= currentIdx ? "text-[var(--pf-text)]" : "text-[var(--pf-text-2)]"}`}>{s.replace('_',' ')}</div>
              </div>
            ))}
          </div>
          {user?.role === "admin" && (
            <div className="mt-6 flex flex-wrap gap-2">
              {STATUSES.map(s => (
                <button key={s} onClick={()=>updateStatus(s)} data-testid={`status-${s}`}
                  className={`px-3 py-1.5 border text-xs uppercase tracking-widest font-bold ${order.status===s?"bg-[var(--pf-text)] text-white":"border-[var(--pf-border)] hover:border-[var(--pf-text)]"}`}>
                  {s.replace('_',' ')}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Design preview */}
          <div className="lg:col-span-5">
            <div className="pf-label mb-3">Approved design</div>
            {design ? (
              <div className="border border-[var(--pf-border)] p-3">
                <img src={`data:image/png;base64,${design.image_base64}`} alt="approved" className="w-full aspect-square object-cover" />
              </div>
            ) : <div className="pf-label">—</div>}
          </div>

          {/* Invoice */}
          <div className="lg:col-span-7">
            <div className="pf-label mb-3">Invoice</div>
            {invoice && (
              <div className="border border-[var(--pf-text)] p-6" data-testid="invoice-block">
                <div className="flex justify-between border-b border-[var(--pf-border)] pb-4 mb-4">
                  <div>
                    <div className="font-black text-xl tracking-tighter">TALAIO/AI</div>
                    <div className="pf-label">GST Invoice</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold">{invoice.invoice_no}</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-1">{new Date(invoice.date).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="mb-6">
                  <div className="pf-label mb-1">Billed to</div>
                  <div className="font-semibold">{invoice.billed_to.name}</div>
                  <div className="text-sm text-[var(--pf-text-2)]">{invoice.billed_to.company} · {invoice.billed_to.email}</div>
                </div>
                <table className="w-full text-sm">
                  <thead className="border-b border-[var(--pf-border)]">
                    <tr>
                      <th className="text-left pb-2 pf-label">Item</th>
                      <th className="text-right pb-2 pf-label">Qty</th>
                      <th className="text-right pb-2 pf-label">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[var(--pf-border)]">
                      <td className="py-3 capitalize">{invoice.product.replace('_',' ')} — print-ready design</td>
                      <td className="py-3 text-right font-mono">{invoice.quantity}</td>
                      <td className="py-3 text-right font-mono">₹{invoice.subtotal.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-4 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">₹{invoice.subtotal.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>GST (18%)</span><span className="font-mono">₹{invoice.gst_18.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t border-[var(--pf-text)] mt-2 pt-2 font-black text-lg">
                    <span>Total</span><span className="font-mono">₹{invoice.total.toLocaleString()}</span>
                  </div>
                </div>
                <div className="mt-6 pf-label">Delivery address</div>
                <div className="text-sm mt-1">{invoice.delivery_address}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
